//#region IMPORTS
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  isAnyOf,
} from "@reduxjs/toolkit";
import {
  InstoreType,
  InstoreTypeState,
  InstoreTypeParams,
} from "../../models/instoreType";
import { FieldValues } from "react-hook-form";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";
//#endregion

//#region INIT
const initialExtraState: InstoreTypeState = {
  instoreTypes_Loaded: false,
  instoreType_params: initParams(),
  metaData: undefined,
  loading: false,
  status: "iddle",
};

// Set initial query parameters
function initParams(): InstoreTypeParams {
  return {
    page: 1,
    limit: 10000,
  };
}
//#endregion

//#region CREATE ADAPTER
const instoreTypesAdapter = createEntityAdapter<InstoreType>();
//#endregion

//#region READ
// Fetch All
export const fetchInstoreTypesAsync = createAsyncThunk<
  InstoreType[],
  void,
  { state: RootState }
>("instoreTypes/fetchInstoreTypesAsync", async (_, thunkAPI) => {
  const instoreTypeParams = getAxiosParams(
    thunkAPI.getState().instoreTypes.instoreType_params
  );
  try {
    const { data, meta } = await agent.InstoreTypes.list(instoreTypeParams);
    thunkAPI.dispatch(setInstoreTypeMetaData(meta));
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});

// Fetch by id
export const fetchInstoreTypeAsync = createAsyncThunk<
  InstoreType,
  number,
  { state: RootState }
>("instoreTypes/fetchInstoreTypeAsync", async (instoreType_id, thunkAPI) => {
  try {
    const { data } = await agent.InstoreTypes.detail(instoreType_id);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});
//#endregion

//#region CREATE
export const createInstoreTypeAsync = createAsyncThunk<
  InstoreType,
  { values: FieldValues | null }
>("instoreTypes/createInstoreTypeAsync", async ({ values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreTypes.create({ ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});
//#endregion

//#region UPDATE
export const updateInstoreTypeAsync = createAsyncThunk<
  InstoreType,
  { id: number; values: FieldValues }
>("instoreTypes/updateInstoreTypeAsync", async ({ id, values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreTypes.update(id, { ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});
//#endregion

//#region DELETE
export const deleteInstoreTypeAsync = createAsyncThunk<number, { id: number }>(
  "instoreTypes/deleteInstoreTypeAsync",
  async ({ id }, thunkAPI) => {
    try {
      await agent.InstoreTypes.delete(id);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data });
    }
  }
);
//#endregion

//#region CREATE REDUCER SLICE
export const instoreTypeSlice = createSlice({
  name: "instoreTypes",
  initialState:
    instoreTypesAdapter.getInitialState<InstoreTypeState>(initialExtraState),
  reducers: {
    setInstoreTypeParams: (state, action) => {
      state.instoreTypes_Loaded = false;
      state.instoreType_params = {
        ...state.instoreType_params,
        ...action.payload,
        page: 1,
      };
    },
    clearInstoreTypeParams: (state) => {
      state.instoreTypes_Loaded = false;
      state.instoreType_params = initParams();
    },
    clearInstoreTypeParam: (state, action) => {
      const newParmams = { ...state.instoreType_params };
      delete newParmams[action.payload];
      state.instoreTypes_Loaded = false;
      state.instoreType_params = newParmams;
    },
    setInstoreTypePageNumber: (state, action) => {
      state.instoreTypes_Loaded = false;
      state.instoreType_params = {
        ...state.instoreType_params,
        ...action.payload,
      };
    },
    setInstoreTypeMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    resetInstoreTypeParams: (state) => {
      state.instoreTypes_Loaded = false;
      state.instoreType_params = initParams();
    },
    clearInstoreTypes: () =>
      instoreTypesAdapter.getInitialState<InstoreTypeState>(initialExtraState),
  },
  extraReducers: (builder) => {
    // FETCH instoreTypes
    builder.addCase(fetchInstoreTypesAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreTypes";
    });
    builder.addCase(fetchInstoreTypesAsync.fulfilled, (state, action) => {
      instoreTypesAdapter.setAll(state, action.payload);
      state.instoreTypes_Loaded = true;
      state.loading = false;
      state.status = "idle";
    });

    // FETCH InstoreType by id
    builder.addCase(fetchInstoreTypeAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreType";
    });
    builder.addCase(fetchInstoreTypeAsync.fulfilled, (state, action) => {
      instoreTypesAdapter.upsertOne(state, action.payload);
      state.loading = false;
      state.status = "idle";
    });

    // CREATE InstoreType
    builder.addCase(createInstoreTypeAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingCreateInstoreType";
    });
    builder.addCase(createInstoreTypeAsync.fulfilled, (state, action) => {
      instoreTypesAdapter.addOne(state, action.payload);
      toast.success("Instore theme added");
      state.loading = false;
      state.status = "idle";
    });

    // UPDATE InstoreType
    builder.addCase(updateInstoreTypeAsync.pending, (state, action) => {
      state.loading = true;
      state.status = "PendingUpdateInstoreType" + action.meta.arg.id;
    });
    builder.addCase(updateInstoreTypeAsync.fulfilled, (state, action) => {
      instoreTypesAdapter.upsertOne(state, action.payload);
      toast.success(`Instore theme ${action.payload.id} updated`);
      state.loading = false;
      state.status = "idle";
    });

    // DELETE InstoreType
    builder.addCase(deleteInstoreTypeAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingDeleteInstoreType";
    });
    builder.addCase(deleteInstoreTypeAsync.fulfilled, (state, action) => {
      instoreTypesAdapter.removeOne(state, action.payload);
      toast.success("Instore theme deleted");
      state.loading = false;
      state.status = "idle";
    });

    //#region CRUD REJECTION
    builder.addMatcher(
      isAnyOf(
        fetchInstoreTypeAsync.rejected,
        fetchInstoreTypesAsync.rejected,
        createInstoreTypeAsync.rejected,
        updateInstoreTypeAsync.rejected,
        deleteInstoreTypeAsync.rejected
      ),
      (state, { payload }) => {
        toast.error(`${payload}`);
        state.loading = false;
        state.status = "idle";
      }
    );
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const instoreTypesSelectors = instoreTypesAdapter.getSelectors(
  (state: RootState) => state.instoreTypes
);
//#endregion

//#region EXPORT
export const {
  setInstoreTypePageNumber,
  setInstoreTypeMetaData,
  resetInstoreTypeParams,
  clearInstoreTypeParams,
  clearInstoreTypeParam,
  setInstoreTypeParams,
  clearInstoreTypes,
} = instoreTypeSlice.actions;
//#endregion
